import React, { useMemo } from "react";
import { isStartListStatus, isLiveStatus } from "helpers/status";
import LoaderSubComponent from "components/general/LoaderSubComponent";
import MirsTable from "components/general/table/Table";
import CaptionTable from "components/general/table/CaptionTable";
//General
import Status from "components/presentation/Status";
import { filterArray } from 'helpers/utils';
import { getIsIndividual } from 'components/sports/div/helper/DivHelpers'
import Label from "components/general/Label"
//Current and previous component
import CurrentCompetitor from 'components/sports/div/views/CurrentCompetitor'
import PreviousCompetitor from 'components/sports/div/views/PreviousCompetitor'
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";


//INDIVIDUALS 
import { TableColumns as ColumnsIndividualStart, TableColumnsSM as ColumnsIndividualStartSM } from 'components/sports/div/events/individual/start/Columns'
import {
    TableColumnsLive as ColumnsIndividual,
    TableColumnsLiveSM as ColumnsIndividualSM,
    TableColumnsFinish as ColumnsIndividualRes,
    TableColumnsFinishSM as ColumnsIndividualResSM
} from 'components/sports/div/events/individual/results/Columns'
import { TableExpanded as TBEIndividual } from 'components/sports/div/events/individual/results/TableExpanded'

//Synchro 
import { TableColumns as ColumnsSynchroStart, TableColumnsSM as ColumnsSynchroStartSM } from 'components/sports/div/events/synchronised/start/Columns'
import {
    TableColumnsLive as ColumnsSynchro,
    TableColumnsLiveSM as ColumnsSynchroSM,
    TableColumnsFinish as ColumnsSynchroRes,
    TableColumnsFinishSM as ColumnsSynchroResSM
} from 'components/sports/div/events/synchronised/results/Columns'
import { TableExpanded as TBESynchro } from 'components/sports/div/events/synchronised/results/TableExpanded'


//import TableMin from "components/table/TableMin";
import { useViewport } from 'components/general/useViewPort'

//Progression Rule
import DivProgressionRule from 'components/sports/div/views/DivProgressionRule'
import NotAvailable from "components/common/NotAvailable";


const getConfiguration = (data, rsc, resultStatus, breakPoint, caption) => {

    //determinar si la configuacion viene del config o del result depende del deporte
    const columnsAdded = data?.config?.extendedConfig ? data.config.extendedConfig.sort((a, b) => a.pos - b.pos) : []
    const isStartList = isStartListStatus(resultStatus) ? true : false
    const isLive = isLiveStatus(resultStatus) ? true : false
    const isIndividual = getIsIndividual(rsc);

    let configuration = {
        caption: null,
        columns: [],
        //subComponent: null,
        hiddenColumns: [],
        sortBy: [],
        filters: {
            ei_type: ['NONE'],
            ei_code: ['NONE']
        },
        configs: {
            isStartList: isStartList,
            columnsAdded: columnsAdded,
            previousCurrent: false,
        }
    };



    configuration.sortBy = isStartList
        ? [{ id: 'StartOrder', desc: false }]
        : isLive
            ? [{ id: 'Rank', desc: false }]
            : [{ id: 'Rank', desc: false }]

    configuration.filters = {
        ei_type: ['DISPLAY'],
        ei_code: ['CURRENT']
    };


    if (isIndividual) {
        configuration.columns = isStartList
            ? breakPoint === "sm" || breakPoint === "xs"
                ? ColumnsIndividualStartSM()
                : ColumnsIndividualStart()
            : isLive
                ? breakPoint === "sm" || breakPoint === "xs"
                    ? ColumnsIndividualSM()
                    : ColumnsIndividual()
                : breakPoint === "sm" || breakPoint === "xs"
                    ? ColumnsIndividualResSM()
                    : ColumnsIndividualRes();
    } else {
        //Synchro
        configuration.columns = isStartList
            ? breakPoint === "sm" || breakPoint === "xs"
                ? ColumnsSynchroStartSM()
                : ColumnsSynchroStart()
            : isLive
                ? breakPoint === "sm" || breakPoint === "xs"
                    ? ColumnsSynchroSM()
                    : ColumnsSynchro()
                : breakPoint === "sm" || breakPoint === "xs"
                    ? ColumnsSynchroResSM()
                    : ColumnsSynchroRes();

    }
    return configuration;
};



const DivResults = ({ data, caption }) => {
    const { mirs_rsc } = useUrlFriendlyToParameters()
    const { breakPoint } = useViewport();
    const resultStatus = data?.results?.status?.code;

    //Cuando cambimos de estado de resultados forzamos a repintar la tabla con las nuevas columnas 
    //aunque no hay cambios    
    // eslint-disable-next-line
    let moreCaption;

    if (isLiveStatus(resultStatus)) {
        let diveTot = data?.config?.extendedConfig?.find(row => row?.code === 'DIVE_TOT')?.value;
        let currentDive = data?.results?.extendedInfos?.find(row => row?.ei_code === 'ROUND_CURRENT')?.ei_value;
        moreCaption = diveTot && currentDive && (<>
            - <Label data="HeaderRound" />: {currentDive} / {diveTot}
        </>)
    }

    let captionComponent = <CaptionTable
        leftCol={<>{caption} {moreCaption}</>}
        rightCol={<Status typeToPrint='result' scheduleStatus={data?.results?.schedule?.status?.code} resultStatus={resultStatus} />}
    />



    const configEvent = useMemo(() => getConfiguration(data, mirs_rsc, resultStatus, breakPoint, caption), [resultStatus, breakPoint]);

    // Create a function that will render our row sub components
    const renderRowSubComponent = React.useCallback(({ row }) => {

        let componentExpanded = '';
        if (getIsIndividual(mirs_rsc)) {
            componentExpanded = <TBEIndividual row={row?.original} config={configEvent?.configs?.columnsAdded} />
        } else {
            componentExpanded = < TBESynchro row={row?.original} config={configEvent?.configs?.columnsAdded} />
        }
        return (
            <LoaderSubComponent component={componentExpanded} />
        )
    }, [resultStatus]);


    const items = data?.results?.items
    const showAll = items?.some(item => item?.eventUnitEntries?.some(item => item?.eue_pos === '1'));
    const hasConfiguration = configEvent.configs.columnsAdded;


    let _playersCurrents = filterArray(data?.results?.extendedInfos, { ei_code: ['CURRENT'] });
    const playersCurrents = _playersCurrents && _playersCurrents.map((player) =>
        player.ei_value
    );

    let _playersPrevious = filterArray(data?.results?.extendedInfos, { ei_code: ['LAST_COMP'] });
    const playersPrevious = _playersPrevious && _playersPrevious.map((player) =>
        player.ei_value
    );


    //Si el contenido de una columna esta vacia la ocultamos(ver casos)
    //Pasamos el id de la columna a ocultar definido en la configuracón de
    let hiddenColumns = []
    if (!hasConfiguration) hiddenColumns.push('Expander')

    const showQualMark = items?.some(item => !item?.qualificationMark);
    if (!showQualMark) hiddenColumns.push('QualMark')


    if (!showAll && !hasConfiguration) hiddenColumns.push('Expander')

    return (
        <>
            {isLiveStatus(resultStatus) && data?.results.previousCompetitorResult ? (
                <PreviousCompetitor data={data?.results?.previousCompetitorResult} />
            ) : ""}

            {isLiveStatus(resultStatus) && data?.results.currentCompetitorResult ? (
                <CurrentCompetitor data={data?.results?.currentCompetitorResult} />
            ) : ""}

            {data?.config ? (
                <DivProgressionRule config={data?.config} />
            ) : ""}


            {data?.results ? (
                <>
                    {data?.results?.items ? (
                        <MirsTable
                            className='table-result'
                            //Dato to show by row
                            data={data.results.items}
                            //Columnas a visualizar 
                            columns={configEvent.columns}
                            //subcomponente a renderizar, extended
                            renderRowSubComponent={renderRowSubComponent}
                            //pageSizeCustom={data.items.length} //ponerlo por defecto en el componente                             
                            //Key by row necesary in order to no paint innecesary
                            keyRow='participant-code'
                            //Splits to show
                            //current split (initial)
                            //Component caption
                            caption={captionComponent}
                            //Columns to hidde 
                            hiddenColumns={hiddenColumns}
                            //When show tble is sorted by 
                            initialSortBy={configEvent.sortBy}
                            //Show buttons move to splits 
                            showNavExtendedData={true}
                            id={`mirs-table-result-${mirs_rsc}`}
                            getRowProps={row => ({
                                className: playersCurrents.includes(row?.original?.participant?.code) ? 'currentIndicator' : '',
                            })}

                            hasRelatedTimeline={true}
                        />
                    ) : ('')
                    }

                </>
            ) : (
                <NotAvailable />
            )
            }

        </>
    );
};

export default DivResults;
